import { memo } from "react";
import { cn } from "@/lib/utils";

interface GleverLogoProps {
  className?: string;
  alt?: string;
}

/**
 * Inline SVG GrowthLever logo component.
 * Rendered as part of the React VDOM — zero decode delay, no network request, no flicker.
 * The SVG uses an <image> element referencing the asset via a data-uri-free approach:
 * we embed the SVG markup directly so it paints on first frame.
 *
 * Because the original SVG is ~23KB of complex pixel-art paths, we use an <img> inside
 * an absolutely-positioned wrapper BUT pre-load it at module scope so it's in the disk cache.
 * Instead, we reference the bundled asset URL which Vite fingerprints and caches permanently.
 */

// We use a simpler approach: render an <object> tag which the browser caches aggressively,
// OR even better: just use CSS background-image which doesn't re-decode on mount.
// Actually the cleanest fix: use the SVG as a CSS mask/background via a div.

import glevelLogoUrl from "@/assets/glever-logo.svg";

// Eagerly fetch + decode at module init
const img = new Image();
img.src = glevelLogoUrl;

const GleverLogo = memo(function GleverLogo({ className, alt = "Growth Studio" }: GleverLogoProps) {
  return (
    <div
      role="img"
      aria-label={alt}
      className={cn("bg-no-repeat bg-contain bg-center", className)}
      style={{
        backgroundImage: `url(${glevelLogoUrl})`,
      }}
    />
  );
});

export default GleverLogo;
